import {CrudModel} from '@ngx-ivengi/crud';
import {assignmentNumberingType} from '@brands-compart/form-wizard';
import {Building} from '../building/building.model';
import {Customer} from '../customer/customer.model';
import {Floor} from '../floor/floor.model';
import {Room} from '../room/room.model';
import {Assignment} from '../assignment/assignment.model';
import {Contact} from '../contact/contact.model';
import { ChannelCurrentState } from './channel-current-state';

export class Channel extends CrudModel {
  public assignmentId: string | number;
  public assignment: Assignment;
  public assignmentType: string;
  public buildingId: string | number;
  public building: Building;
  public floorId: string | number;
  public floor: Floor;
  public location: string;
  public customerContactId: string;
  public customerContact: Contact | string;
  public customer: Customer | string;
  public roomFromId: string | number;
  public roomFrom: Room | string;
  public roomToId: string | number;
  public roomTo: Room | string;
  public recordingFormat: string;
  public registrationDate: Date;
  public label: string;
  public customLabel: string;
  public numbering: assignmentNumberingType;
  public mechanics: string[];
  public type: string;
  public shape: string;
  public width: number;
  public height: number;
  public diameter: number;
  public finish: string;
  public remarks: string;
  public internalRemarks: string;
  public chargeInclude: boolean = false;
  public chargeUnit: string;
  public chargeAmount: number;
  public manualCalculation: boolean = false;
  public manualPrice?: number;
  public dimension: string;
  public explanation: string;
  public throughputCount: number;
  public coordinate: [number, number];
  public hasCoordinate: boolean = false;
  public overridden: boolean = false;
  public currentState?: ChannelCurrentState;

  public setAssignmentId(assignmentId: string | number): this {
    if(typeof assignmentId === 'string') {
      this.assignmentId = assignmentId;
    }
    return this;
  }

  public setAssignment(assignment: Assignment): this {
    if(assignment instanceof Assignment) {
      this.assignment = assignment;
    }
    return this;
  }

  public setAssignmentType(assignmentType: string): this {
    if(typeof assignmentType === 'string') {
      this.assignmentType = assignmentType;
    }
    return this;
  }

  public setChannelType(type: string): this {
    if(typeof type === 'string') {
      this.type = type;
    }
    return this;
  }

  public setBuildingId(buildingId: string | number): this {
    if(typeof buildingId === 'string') {
      this.buildingId = buildingId;
    }
    return this;
  }

  public setBuilding(building: Building): this {
    if(building instanceof Building) {
      this.building = building;
    }
    return this;
  }

  public setFloorId(floorId: string | number): this {
    if(typeof floorId === 'string') {
      this.floorId = floorId;
    }
    return this;
  }

  public setFloor(floor: Floor): this {
    if(floor instanceof Floor) {
      this.floor = floor;
    }
    return this;
  }

  public setLocation(location: string): this {
    if(typeof location === 'string') {
      this.location = location;
    }
    return this;
  }

  public setCustomerContactId(customerId: string): this {
    if(typeof customerId === 'string') {
      this.customerContactId = customerId;
    }
    return this;
  }

  public setCustomerContact(customerContact: Contact | string): this {
    if(customerContact instanceof Contact) {
      this.customerContact = customerContact;
    } else if(!this.customerContact && typeof customerContact === 'string') {
      this.customerContact = customerContact;
    }
    return this;
  }

  public setCustomer(customer: Customer): this {
    if(customer instanceof Customer) {
      this.customer = customer;
    }

    return this;
  }

  public setRoomFromId(roomId: string | number): this {
    if(typeof roomId === 'string' && roomId.length > 0) {
      this.roomFromId = roomId;
    }
    return this;
  }

  public setRoomFrom(room: Room): this {
    if(room instanceof Room) {
      this.roomFrom = room;
    } else if(typeof room === 'object') {
      this.roomFrom = new Room();
      this.roomFrom.fromResponse(room);
    } else if(!this.roomFrom && typeof room === 'string' && (<string>room).length > 0) {
      this.roomFrom = room;
    }
    return this;
  }

  public setRoomToId(roomId: string | number): this {
    if(typeof roomId === 'string' && roomId.length > 0) {
      this.roomToId = roomId;
    }
    return this;
  }

  public setRoomTo(room: Room): this {
    if(room instanceof Room) {
      this.roomTo = room;
    } else if(typeof room === 'object') {
      this.roomTo = new Room();
      this.roomTo.fromResponse(room);
    } else if(!this.roomTo && typeof room === 'string' && (<string>room).length > 0) {
      this.roomTo = room;
    }
    return this;
  }

  public setRecordingFormat(recordingFormat: string): this {
    if(typeof recordingFormat === 'string') {
      this.recordingFormat = recordingFormat;
    }
    return this;
  }

  public setRegistrationDate(registrationDate: Date | string): this {
    if(registrationDate instanceof Date) {
      this.registrationDate = registrationDate;
    } else if(typeof registrationDate === 'string') {
      const matches = registrationDate.match(/([0-9]{4})-([0-9]{2})-([0-9]{2})/);
      if(matches) {
        const dateObj = new Date();
        dateObj.setFullYear(parseInt(matches[1], 10));
        dateObj.setMonth((parseInt(matches[2], 10) - 1));
        dateObj.setDate(parseInt(matches[3], 10));
        this.registrationDate = dateObj;
      }
    }

    return this;
  }

  public setLabel(label: string): this {
    if(typeof label === 'string') {
      this.label = label;
    }
    return this;
  }

  public setNumbering(numbering: assignmentNumberingType): this {
    if(typeof numbering === 'string') {
      this.numbering = numbering;
    }
    return this;
  }

  public setCustomLabel(label: string): this {
    if(typeof label === 'string') {
      this.customLabel = label;
    }
    return this;
  }

  public setMechanics(mechanics: string[]): this {
    if(mechanics instanceof Array) {
      this.mechanics = mechanics.filter((mechanic) => {
        return (typeof mechanic === 'string');
      });
    }
    return this;
  }

  public setShape(shape: string): this {
    if(typeof shape === 'string') {
      this.shape = shape;
    }
    return this;
  }

  public setWidth(width: number): this {
    if(typeof width === 'number') {
      this.width = width;
    } else if(typeof width === 'string') {
      const int = parseInt(width, 10);
      if(!isNaN(int)) {
        this.width = int;
      }
    }
    return this;
  }

  public setHeight(height: number): this {
    if(typeof height === 'number') {
      this.height = height;
    } else if(typeof height === 'string') {
      const int = parseInt(height, 10);
      if(!isNaN(int)) {
        this.height = int;
      }
    }
    return this;
  }

  public setDiameter(diameter: number): this {
    if(typeof diameter === 'number') {
      this.diameter = diameter;
    } else if(typeof diameter === 'string') {
      const int = parseInt(diameter, 10);
      if(!isNaN(int)) {
        this.diameter = int;
      }
    }
    return this;
  }

  public setFinish(finish: string): this {
    if(typeof finish === 'string') {
      this.finish = finish;
    }
    return this;
  }

  public setRemarks(remarks: string): this {
    if(typeof remarks === 'string') {
      this.remarks = remarks;
    }
    return this;
  }

  public setInternalRemarks(internalRemarks: string): this {
    if(typeof internalRemarks === 'string') {
      this.internalRemarks = internalRemarks;
    }
    return this;
  }

  public setChargeInclude(chargeInclude: boolean): this {
    if(typeof chargeInclude ===  'boolean') {
      this.chargeInclude = chargeInclude;
    }
    return this;
  }

  public setChargeUnit(chargeUnit: string): this {
    if(typeof chargeUnit === 'string') {
      this.chargeUnit = chargeUnit;
    }
    return this;
  }

  public setChargeAmount(chargeAmount: number): this {
    if(typeof chargeAmount === 'number') {
      this.chargeAmount = chargeAmount;
    } else if(typeof chargeAmount === 'string') {
      if(!isNaN(parseFloat(chargeAmount))) {
        this.chargeAmount = parseFloat(chargeAmount);
      }
    }
    return this;
  }

  public setDimension(dimension: string): this {
    if(typeof dimension === 'string') {
      this.dimension = dimension;
    }
    return this;
  }

  public setExplanation(explanation: string): this {
    if(typeof explanation === 'string') {
      this.explanation = explanation;
    }
    return this;
  }

  public setThroughputCount(throughputCount: number): this {
    if(typeof(throughputCount) === 'number') {
      this.throughputCount = throughputCount;
    }
    return this;
  }

  public setCoordinate(coordinate: any): this {
    if(coordinate && typeof coordinate === 'object' && coordinate.coordinates instanceof Array && coordinate.coordinates.length === 2) {
      this.coordinate = coordinate.coordinates;
      this.hasCoordinate = !!(coordinate.coordinates[0] !== 0 && coordinate.coordinates[1] !== 0);
    }
    return this;
  }

  public setOverridden(overridden: boolean) {
    if(typeof overridden === 'boolean') {
      this.overridden = overridden;
    }
    return this;
  }

  public setManualCalculation(manualCalculation: boolean): this {
    if(typeof manualCalculation === 'boolean') {
      this.manualCalculation = manualCalculation;
    }
    return this;
  }

  public setManualPrice(manualPrice: number): this {
    if(typeof manualPrice === 'number') {
      this.manualPrice = manualPrice;
    }
    return this;
  }

  public fromResponse(response: any): void {

    if(response.building) {
      const building = new Building();
      building.fromResponse(response.building);
      this.setBuilding(building);
    }

    if(response.floor) {
      const floor = new Floor();
      floor.fromResponse(response.floor);
      this.setFloor(floor);
    }

    let customerContact;
    let customer;

    if(typeof response.customerContact === 'object') {
      customerContact = new Contact();
      customerContact.fromResponse(response.customerContact);

      if(typeof response.customerContact.customer === 'object') {
        customer = new Customer();
        customer.fromResponse(response.customerContact.customer);
      }

    } else {
      this.customer = response.customerContactId;
      customerContact = response.customerContactId;
    }

    if(response.roomFrom) {
      this.setRoomFrom(response.roomFrom);
    } else {
      this.setRoomFrom(response.roomFromId);
    }

    if(response.roomTo) {
      this.setRoomTo(response.roomTo);
    } else {
      this.setRoomTo(response.roomToId);
    }

    if(response.mechanics instanceof Array) {
      response.mechanics = response.mechanics.map((mechanic) => {
        if(typeof mechanic === 'object') {
          return mechanic.id;
        } else {
          return mechanic;
        }
      });
    }

    this
      .setId(response.id)
      .setAssignmentId(response.assignmentId)
      .setAssignmentType(response.assignmentType)
      .setChannelType(response.type)
      .setBuildingId(response.buildingId)
      .setFloorId(response.floorId)
      .setLocation(response.location)
      .setCustomerContactId(response.customerContactId)
      .setCustomerContact(customerContact)
      .setCustomer(customer)
      .setRoomFrom(response.roomFrom)
      .setRoomFromId(response.roomFromId)
      .setRoomTo(response.roomTo)
      .setRoomToId(response.roomToId)
      .setRecordingFormat(response.recordingFormat)
      .setRegistrationDate(response.registrationDate)
      .setLabel(response.label)
      .setNumbering(response.numbering)
      .setMechanics(response.mechanics)
      .setShape(response.shape)
      .setWidth(response.width)
      .setHeight(response.height)
      .setDiameter(response.diameter)
      .setFinish(response.finish)
      .setRemarks(response.remarks)
      .setInternalRemarks(response.internalRemarks)
      .setChargeInclude(response.chargeInclude)
      .setChargeUnit(response.chargeUnit)
      .setChargeAmount(response.chargeAmount)
      .setDimension(response.dimension)
      .setExplanation(response.explanation)
      .setThroughputCount(response.throughputCount)
      .setCoordinate(response.coordinate)
      .setOverridden(response.overridden)
      .setManualCalculation(response.manualCalculation)
      .setManualPrice(response.manualPrice);
  }

  toRequest(): any {
    this
      .setCustomerContactId(<string>this.customer)
      .setRoomFromId(<string>this.roomFrom)
      .setRoomToId(<string>this.roomTo)
      .setChargeAmount(this.chargeAmount)
      .setWidth(this.width)
      .setHeight(this.height)
      .setDiameter(this.diameter);

    return this;
  }

}



// WEBPACK FOOTER //
// /var/jenkins_home/workspace/brands-compart-portal-frontend/checkout/src/app/components/channel/channel.model.ts